import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { TrendingUp, Lock, ShieldCheck, Loader2, AlertCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function Login() {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [step, setStep] = useState("creds"); // creds | mfa
  const [mfaToken, setMfaToken] = useState("");
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) navigate("/app/dashboard", { replace: true });
  }, [user, navigate]);

  const submitCreds = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.mfa_required) {
        setMfaToken(data.mfa_token);
        setStep("mfa");
      } else {
        setUser(data.user);
        navigate("/app/dashboard", { replace: true });
      }
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const [mfaMethod, setMfaMethod] = useState("totp");

  const submitMfa = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/mfa/verify", { mfa_token: mfaToken, code, method: mfaMethod });
      setUser(data.user);
      navigate("/app/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[var(--bg-canvas)]">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-1/2 p-12 relative overflow-hidden border-r border-[var(--border-subtle)]">
        <div className="absolute inset-0 grid-noise opacity-60" />
        <div
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage:
              "url(https://images.unsplash.com/photo-1516031190212-da133013de50?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200)",
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
        <div className="relative z-10 flex items-center gap-2">
          <div className="w-9 h-9 rounded-md bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
            <TrendingUp className="w-5 h-5 text-white" />
          </div>
          <span className="font-display text-2xl font-bold text-white tracking-tight">Tradify</span>
          <span className="text-[10px] font-mono text-blue-400 border border-blue-500/30 rounded px-1.5 py-0.5">
            PHASE 1
          </span>
        </div>
        <div className="relative z-10">
          <h1 className="font-display text-4xl font-bold text-white leading-tight tracking-tight">
            Private automated<br />trading command center.
          </h1>
          <p className="text-slate-400 text-sm mt-4 max-w-md">
            Multi-asset, multi-broker foundation with GTF demand & supply analysis,
            decimal-safe accounting, and institutional risk controls. Single-owner access only.
          </p>
          <div className="flex gap-2 mt-6">
            <span className="text-[10px] font-mono text-amber-400 bg-amber-500/10 border border-amber-500/30 rounded px-2 py-1 uppercase">
              Demo Mode
            </span>
            <span className="text-[10px] font-mono text-slate-400 bg-slate-500/10 border border-slate-600/40 rounded px-2 py-1 uppercase">
              Live Trading Disabled
            </span>
          </div>
        </div>
        <div className="relative z-10 text-[11px] text-slate-600 font-mono">
          Tradify © 2026 · Not an investment advisory service
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-8 justify-center">
            <div className="w-9 h-9 rounded-md bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <span className="font-display text-2xl font-bold text-white">Tradify</span>
          </div>

          {step === "creds" ? (
            <form onSubmit={submitCreds} data-testid="login-form">
              <h2 className="font-display text-2xl font-bold text-white mb-1">Owner Sign In</h2>
              <p className="text-xs text-slate-500 mb-6">Secure access · No public registration.</p>

              <label className="text-xs font-medium text-slate-400">Email</label>
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="owner@tradify.app"
                required
                className="mt-1.5 mb-4 bg-[var(--surface)] border-[var(--border-active)] text-slate-100"
              />

              <label className="text-xs font-medium text-slate-400">Password</label>
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-1.5 mb-5 bg-[var(--surface)] border-[var(--border-active)] text-slate-100"
              />

              {error && (
                <div data-testid="login-error" className="flex items-center gap-2 mb-4 text-xs text-rose-400 bg-rose-500/10 border border-rose-500/25 rounded-md px-3 py-2">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {error}
                </div>
              )}

              <Button
                data-testid="login-submit"
                type="submit"
                disabled={loading}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-medium"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Lock className="w-4 h-4 mr-2" /> Sign In</>}
              </Button>
            </form>
          ) : (
            <form onSubmit={submitMfa} data-testid="mfa-form">
              <div className="w-12 h-12 rounded-lg bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center mb-4">
                <ShieldCheck className="w-6 h-6 text-emerald-400" />
              </div>
              <h2 className="font-display text-2xl font-bold text-white mb-1">Two-Factor Auth</h2>
              <p className="text-xs text-slate-500 mb-6">
                {mfaMethod === "totp" ? "Enter the 6-digit code from your authenticator app." : "Enter one of your single-use recovery codes."}
              </p>

              <Input
                data-testid="mfa-code"
                value={code}
                onChange={(e) => setCode(mfaMethod === "totp" ? e.target.value.replace(/\D/g, "").slice(0, 6) : e.target.value.toUpperCase().slice(0, 13))}
                placeholder={mfaMethod === "totp" ? "000000" : "XXXXX-XXXXX"}
                inputMode={mfaMethod === "totp" ? "numeric" : "text"}
                required
                className="mb-3 bg-[var(--surface)] border-[var(--border-active)] text-slate-100 text-center text-2xl font-mono-num tracking-[0.3em]"
              />
              <button
                type="button"
                data-testid="toggle-mfa-method"
                onClick={() => { setMfaMethod(mfaMethod === "totp" ? "recovery" : "totp"); setCode(""); setError(""); }}
                className="w-full text-xs text-blue-400 hover:text-blue-300 mb-4"
              >
                {mfaMethod === "totp" ? "Use a recovery code instead" : "Use authenticator code instead"}
              </button>

              {error && (
                <div data-testid="mfa-error" className="flex items-center gap-2 mb-4 text-xs text-rose-400 bg-rose-500/10 border border-rose-500/25 rounded-md px-3 py-2">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {error}
                </div>
              )}

              <Button
                data-testid="mfa-submit"
                type="submit"
                disabled={loading || (mfaMethod === "totp" ? code.length !== 6 : code.trim().length < 4)}
                className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-medium"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Verify & Continue"}
              </Button>
              <button
                type="button"
                onClick={() => { setStep("creds"); setError(""); setCode(""); }}
                className="w-full text-xs text-slate-500 hover:text-slate-300 mt-3"
              >
                ← Back to sign in
              </button>
            </form>
          )}

          <div className="mt-8 text-center text-[11px] text-slate-600 font-mono">
            Protected area · Audit logging enabled
          </div>
        </div>
      </div>
    </div>
  );
}
